import type {
  MfgInventoryChangelogRow,
  MfgInventoryCostEntryType,
} from "@/lib/manufacturing/types";

const ENTRY_TYPE_LABELS: Record<MfgInventoryCostEntryType, string> = {
  receive: "Receive",
  manual: "Manual",
  adjustment: "Adjustment",
  opening_balance: "Opening balance",
  build: "Build",
};

const ENTRY_TYPE_BADGE_CLASSES: Record<MfgInventoryCostEntryType, string> = {
  receive:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  manual: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200",
  adjustment: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  opening_balance:
    "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200",
  build: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
};

export function ManufacturingInventoryChangelogRow({
  row,
  onLinkClick,
}: {
  row: MfgInventoryChangelogRow;
  onLinkClick: () => void;
}) {
  const qtySigned = Number(row.quantity);
  const qtyClass =
    qtySigned >= 0
      ? "text-emerald-700 dark:text-emerald-300"
      : "text-amber-700 dark:text-amber-300";
  const unit = row.material_unit ? ` ${row.material_unit}` : "";
  const hasReceipt = !!row.receipt_id;
  const isRecipe = row.ledger_kind === "recipe";
  return (
    <li className="flex items-start justify-between gap-2 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">
            {row.material_name ?? (isRecipe ? "(unknown recipe)" : "(unknown material)")}
          </span>
          {row.material_code ? (
            <span className="text-[11px] text-gray-500 dark:text-zinc-400">{row.material_code}</span>
          ) : null}
          <span
            className={
              "rounded-full px-2 py-0.5 text-[10px] font-semibold " +
              (isRecipe
                ? "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
                : "bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-300")
            }
          >
            {isRecipe ? "Recipe" : "Material"}
          </span>
          <span
            className={
              "rounded-full px-2 py-0.5 text-[10px] font-semibold " +
              ENTRY_TYPE_BADGE_CLASSES[row.entry_type]
            }
          >
            {ENTRY_TYPE_LABELS[row.entry_type]}
          </span>
          {hasReceipt && row.receipt_public_url ? (
            <a
              href={row.receipt_public_url}
              target="_blank"
              rel="noreferrer"
              title={row.receipt_vendor_name ?? "Linked receipt"}
              className="rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-950/35 dark:text-indigo-200 dark:hover:bg-indigo-900/40"
            >
              receipt: {row.receipt_vendor_name?.slice(0, 18) || "open"}
            </a>
          ) : hasReceipt ? (
            <span className="rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800 dark:border-indigo-800 dark:bg-indigo-950/35 dark:text-indigo-200">
              receipt linked
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 text-xs text-gray-700 dark:text-zinc-300">
          {row.notes.startsWith("On-hand price") ? (
            <span className="font-medium text-gray-800 dark:text-zinc-200">
              {row.notes.split(". ")[0]}
            </span>
          ) : (
            <>
              <span className={`tabular-nums font-medium ${qtyClass}`}>
                {qtySigned >= 0 ? "+" : ""}
                {qtySigned.toFixed(2)}
                {unit}
              </span>{" "}
              @ <span className="tabular-nums">${Number(row.unit_cost).toFixed(2)}</span>
            </>
          )}
          {" · by "}
          {row.posted_by_label}
        </div>
        {row.notes && !row.notes.startsWith("On-hand price") ? (
          <div className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-zinc-400">
            {row.notes}
          </div>
        ) : row.notes.includes(". ") ? (
          <div className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-zinc-400">
            {row.notes.slice(row.notes.indexOf(". ") + 2)}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-[11px] text-gray-500 dark:text-zinc-500">
        <span>{new Date(row.effective_at).toLocaleString()}</span>
        {isRecipe ? null : (
          <button
            type="button"
            onClick={onLinkClick}
            className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
          >
            {hasReceipt ? "Change" : "Link"}
          </button>
        )}
      </div>
    </li>
  );
}
